import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

export type Ips = components['schemas']['IpsRead']
export type SolicitudIps = components['schemas']['SolicitudIpsRead']
export type CargosListos = components['schemas']['CargosListosRead']
export type CargoListo = components['schemas']['CargoListoRead']
export type Previsualizacion = components['schemas']['PrevisualizacionRead']
export type LoteSolicitudes = components['schemas']['LoteSolicitudesRead']
export type DocumentoSolicitud = components['schemas']['DocumentoRead']
export type SobreDeEnvio = components['schemas']['EnvioRead']
export type EnlaceCarga = components['schemas']['EnlaceCargaRead']
export type MedioDeEnvio = components['schemas']['SolicitudIpsMedio']
export type TipoEvaluacion = components['schemas']['SolicitudIpsTipo']
export type PruebaAutorizada = {prueba: string; origen_texto: string}
export type Literal = 'b' | 'c' | 'd' | 'e'
/** Un documento del art. 16 b–e elegido al preparar: la evidencia ya subida y la fecha del documento. */
export type AdjuntoArt16 = {literal: Literal; evidence_id: string; filename: string; fecha: string}

/** Vive aquí porque la lista y el detalle pintan el mismo dato; el `Record` sobre la unión
 * cerrada hace que un estado nuevo rompa el build en vez de caer a un tono por defecto. */
export const ESTADO_TONE: Record<SolicitudIps['estado'], 'green' | 'amber' | 'red' | 'enCurso'> = {
  generada: 'amber',
  enviada: 'enCurso',
  entregada_en_mano: 'enCurso',
  entregada_buzon: 'enCurso',
  enviada_sin_acuse: 'amber',
  sin_confirmacion_entrega: 'amber',
  devuelta: 'red',
  ips_pregunto: 'amber',
  respuesta_sin_identificar: 'amber',
  con_concepto: 'green',
  comparada: 'green',
}

export const IPS_PAGE_SIZE = 10
export const SOLICITUDES_PAGE_SIZE = 10
export const CARGOS_PAGE_SIZE = 20

function ipsBase(businessId: string | undefined) {
  return `/businesses/${businessId}/ips`
}

function base(businessId: string | undefined) {
  return `/businesses/${businessId}/solicitudes-ips`
}

/** El estado de la licencia lo deriva la API: la pantalla no vuelve a comparar fechas. */
export function useIpsList(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<Ips>(['ips', businessId], businessId ? ipsBase(businessId) : undefined, params, IPS_PAGE_SIZE)
}

export function useCrearIps(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {name: string; correoInstitucional: string}) =>
      (await api.post<Ips>(ipsBase(businessId), {name: input.name, correo_institucional: input.correoInstitucional}))
        .data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['ips', businessId]})
      track('ips_registrada', {business_id: businessId})
    },
  })
}

/** El servidor registra quién y cuándo cambia el correo institucional. Los datos de la
 * licencia se confirman aquí a mano: el lector de IA solo propone. */
export function useCorregirIps(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      ipsId: string
      name?: string
      correoInstitucional?: string
      licenciaNumero?: string
      licenciaTitular?: string
      licenciaEntidad?: string
      licenciaVenceEl?: string
    }) =>
      (
        await api.patch<Ips>(`${ipsBase(businessId)}/${input.ipsId}`, {
          name: input.name ?? null,
          correo_institucional: input.correoInstitucional ?? null,
          licencia_numero: input.licenciaNumero || null,
          licencia_titular: input.licenciaTitular || null,
          licencia_entidad: input.licenciaEntidad || null,
          licencia_vence_el: input.licenciaVenceEl || null,
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['ips', businessId]}),
  })
}

/** Atestación del empleador sin cuerpo: solo queda quién y cuándo. */
export function useDeclararIps(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ipsId: string) => (await api.post<Ips>(`${ipsBase(businessId)}/${ipsId}/declaracion`, {})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['ips', businessId]})
      track('ips_declarada', {business_id: businessId})
    },
  })
}

/** Sin `listo` salen todos, con los listos primero. */
export function useCargosListos(businessId: string | undefined, params: PagedListParams & {listo?: boolean}) {
  const {listo, ...tabla} = params
  return usePagedList<CargoListo>(
    ['solicitudes-ips', businessId, 'cargos-listos'],
    businessId ? `${base(businessId)}/cargos-listos` : undefined,
    {...tabla, extra: {listo: listo === undefined ? undefined : String(listo)}},
    CARGOS_PAGE_SIZE
  )
}

/** Va aparte de la lista porque las cifras son de toda la empresa, no de la página. */
export function useResumenCargos(businessId: string | undefined) {
  return useQuery({
    queryKey: ['solicitudes-ips', businessId, 'cargos-listos', 'resumen'],
    queryFn: async () => (await api.get<CargosListos>(`${base(businessId)}/cargos-listos/resumen`)).data,
    enabled: !!businessId,
  })
}

/** Mutación y no consulta: se pide al abrir el diálogo, no en cada pintado. */
export function usePrevisualizarSolicitud(businessId: string) {
  return useMutation({
    mutationFn: async (input: {employeeId: string; positionId: string}) =>
      (
        await api.post<Previsualizacion>(`${base(businessId)}/previsualizar`, {
          employee_id: input.employeeId,
          position_id: input.positionId,
        })
      ).data,
  })
}

export function useGenerarLote(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      positionId: string
      ipsId: string | null
      tipo?: TipoEvaluacion | null
      tipoMotivo?: string | null
      bateria?: PruebaAutorizada[]
      adjuntos?: AdjuntoArt16[]
    }) =>
      (
        await api.post<LoteSolicitudes>(`${base(businessId)}/lote`, {
          position_id: input.positionId,
          ips_id: input.ipsId,
          tipo: input.tipo ?? null,
          tipo_motivo: input.tipoMotivo ?? null,
          bateria: input.bateria ?? [],
          adjuntos: (input.adjuntos ?? []).map(({literal, evidence_id, fecha}) => ({literal, evidence_id, fecha})),
        })
      ).data,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({queryKey: ['solicitudes-ips', businessId]})
      track('solicitudes_ips_lote', {
        business_id: businessId,
        position_id: variables.positionId,
        generadas: data.generadas.length,
      })
    },
  })
}

export function useSolicitudesIps(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<SolicitudIps>(
    ['solicitudes-ips', businessId],
    businessId ? base(businessId) : undefined,
    params,
    SOLICITUDES_PAGE_SIZE
  )
}

/** Se pide al hacer clic: cada llamada firma una URL de vida corta. */
export function useDocumentoSolicitud(businessId: string) {
  return useMutation({
    mutationFn: async (solicitudId: string) =>
      (await api.get<DocumentoSolicitud>(`${base(businessId)}/${solicitudId}/documento`)).data,
  })
}

/** PQ-7a. Se pide al abrir el diálogo: la revalidación consulta el organigrama y serían
 * veinte lecturas por tabla. */
export function useSobreDeEnvio(businessId: string, solicitudId: string | null) {
  return useQuery({
    queryKey: ['solicitudes-ips', businessId, 'envio', solicitudId],
    queryFn: async () => (await api.get<SobreDeEnvio>(`${base(businessId)}/${solicitudId}/envio`)).data,
    enabled: !!solicitudId,
  })
}

/** `reenvio` y `motivoCargoCambiado` solo viajan como respuesta a lo que el sobre advirtió. */
export function useEnviarSolicitud(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {solicitudId: string; reenvio?: boolean; motivoCargoCambiado?: string | null}) =>
      (
        await api.post<SolicitudIps>(`${base(businessId)}/${input.solicitudId}/enviar`, {
          reenvio: input.reenvio ?? false,
          motivo_cargo_cambiado: input.motivoCargoCambiado || null,
        })
      ).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: ['solicitudes-ips', businessId]})
      track('solicitud_ips_enviada', {business_id: businessId, solicitud_id: data.id})
    },
  })
}

/** La constancia ya subió como evidencia: solo viaja su id. Los plazos corren desde `enviadaEl`. */
export function useCargarEnviada(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      solicitudId: string
      constanciaEvidenceId: string
      enviadaEl: string
      medio: MedioDeEnvio
      ipsId: string | null
    }) =>
      (
        await api.post<SolicitudIps>(`${base(businessId)}/${input.solicitudId}/cargar-enviada`, {
          constancia_evidence_id: input.constanciaEvidenceId,
          enviada_el: input.enviadaEl,
          medio: input.medio,
          ips_id: input.ipsId,
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['solicitudes-ips', businessId]}),
  })
}

/** PQ-7b: no toca `enviada_en` ni los relojes. El enlace llega una sola vez: se guarda en el
 * estado de la pantalla, nunca en la caché. */
export function useRenovarEnlace(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (solicitudId: string) =>
      (await api.post<EnlaceCarga>(`${base(businessId)}/${solicitudId}/renovar-enlace`, {})).data,
    onSuccess: (_data, solicitudId) => {
      queryClient.invalidateQueries({queryKey: ['solicitudes-ips', businessId]})
      track('solicitud_ips_enlace_renovado', {business_id: businessId, solicitud_id: solicitudId})
    },
  })
}
